import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, X, Loader2, AlertCircle, Lock, Copy, Check, Wand2, Hash,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { generateVideoThumbnails } from '../lib/videoThumbnails';
import { PLATFORM_META, type Platform } from '../lib/platforms';

export type AiCaptionUsage = {
  used: number;
  limit: number;
  remaining: number;
  resetAt: string;
  hasSubscription: boolean;
  planName: string | null;
};

type SelectedAccount = {
  id: string;
  platform: Platform;
  username: string | null;
  displayName: string | null;
};

type Media = { id: string; url: string; kind: 'image' | 'video' };

type Props = {
  open: boolean;
  onClose: () => void;
  /** The accounts currently selected as post targets, in order of selection. The FIRST one is used for tone + brand context. */
  accounts: SelectedAccount[];
  media: Media[];
  /** Inserts the AI-generated caption (and hashtags) back into the composer textarea. */
  onInsert: (text: string) => void;
};

export function AiCaptionModal({ open, onClose, accounts, media, onInsert }: Props) {
  const [phase, setPhase] = useState<'idle' | 'prepping' | 'generating' | 'result' | 'error'>('idle');
  const [usage, setUsage] = useState<AiCaptionUsage | null>(null);
  const [extraInfo, setExtraInfo] = useState('');
  const [result, setResult] = useState<{ caption: string; hashtags: string[]; brandName: string | null; hasBrandContext: boolean } | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const firstAccount = accounts[0] ?? null;

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setPhase('idle');
    setResult(null);
    setErrorMsg(null);
    setErrorCode(null);
    setExtraInfo('');
    setCopied(false);
    api<{ usage: AiCaptionUsage }>('/api/ai/caption-usage')
      .then((r) => setUsage(r.usage))
      .catch(() => setUsage(null));
  }, [open]);

  // Lock body scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const canRun = !!firstAccount && media.length > 0;

  async function run() {
    if (!firstAccount) return;
    setErrorMsg(null);
    setErrorCode(null);
    setResult(null);
    try {
      setPhase('prepping');
      const imageIds: string[] = [];
      const videos: Media[] = [];
      for (const m of media) {
        if (m.kind === 'image') imageIds.push(m.id);
        else videos.push(m);
      }
      const extraImages: string[] = [];
      for (const v of videos.slice(0, 2)) {
        try {
          const frames = await generateVideoThumbnails(v.url, { count: 3, maxSide: 640, quality: 0.78 });
          extraImages.push(...frames);
          if (extraImages.length >= 4) break;
        } catch {/* continue */}
      }

      setPhase('generating');
      const res = await api<{
        id: string;
        caption: string;
        hashtags: string[];
        usage: AiCaptionUsage;
        platform: Platform;
        brandName: string | null;
        hasBrandContext: boolean;
      }>('/api/ai/caption', {
        method: 'POST',
        body: JSON.stringify({
          accountId: firstAccount.id,
          mediaIds: imageIds.slice(0, 4),
          extraImages: extraImages.slice(0, 4),
          extraInfo: extraInfo.trim(),
        }),
      });
      setResult({
        caption: res.caption,
        hashtags: res.hashtags,
        brandName: res.brandName,
        hasBrandContext: res.hasBrandContext,
      });
      setUsage(res.usage);
      setPhase('result');
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorCode(err.code);
        const m = (err as ApiError & { details?: { message?: string } }).details?.message;
        setErrorMsg(m || err.message || 'Algo ha fallado.');
      } else {
        setErrorMsg(err instanceof Error ? err.message : 'Algo ha fallado.');
      }
      setPhase('error');
    }
  }

  if (!open) return null;

  const meta = firstAccount ? PLATFORM_META[firstAccount.platform] : null;
  const Icon = meta?.icon ?? null;

  function fullText(): string {
    if (!result) return '';
    const tags = result.hashtags.length > 0
      ? '\n\n' + result.hashtags.map((h) => `#${h}`).join(' ')
      : '';
    return result.caption + tags;
  }

  async function handleCopy() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(fullText());
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {/* clipboard unavailable */}
  }

  function handleInsert() {
    if (!result) return;
    onInsert(fullText());
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 transition"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-accent-500 to-pink-500 flex items-center justify-center text-white shadow-lg shadow-accent-500/20">
              <Wand2 size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Generar caption con IA</h2>
              <p className="text-xs text-slate-500">Crearemos un caption a partir de tu media, adaptado a la red elegida.</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Pre-run gating */}
          {phase === 'idle' && usage && !usage.hasSubscription && (
            <UpgradeCta title="Necesitas una suscripción activa" subtitle="La generación de captions con IA está disponible con cualquier plan de Zettapost." onClose={onClose} />
          )}
          {phase === 'idle' && usage && usage.hasSubscription && usage.limit <= 0 && (
            <UpgradeCta title="Tu plan no incluye generación de captions" subtitle="Cambia a un plan superior para usar esta función." onClose={onClose} />
          )}
          {phase === 'idle' && usage && usage.hasSubscription && usage.limit > 0 && usage.remaining <= 0 && (
            <UpgradeCta
              title={`Has usado ${usage.used}/${usage.limit} captions este mes`}
              subtitle="Mejora tu plan para tener más generaciones disponibles."
              onClose={onClose}
            />
          )}

          {/* Pre-run form */}
          {phase === 'idle' && (!usage || (usage.hasSubscription && usage.limit > 0 && usage.remaining > 0)) && (
            <div>
              {/* Target */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 mb-5">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Plataforma destino</div>
                {!firstAccount && (
                  <p className="text-sm text-slate-500">Selecciona al menos una red social en el composer para generar un caption.</p>
                )}
                {firstAccount && Icon && meta && (
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center shadow-sm">
                      <Icon size={18} color={meta.color} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900">{meta.label}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {firstAccount.username ? `@${firstAccount.username}` : (firstAccount.displayName ?? 'Cuenta sin nombre')}
                      </div>
                    </div>
                  </div>
                )}
                {accounts.length > 1 && (
                  <p className="mt-3 text-[11px] text-slate-500">
                    Has seleccionado {accounts.length} cuentas. Adaptaremos el tono a la primera ({meta?.label}).
                  </p>
                )}
              </div>

              {/* Media summary */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 mb-5">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Media elegida</div>
                {media.length === 0 && (
                  <p className="text-sm text-slate-500">No has elegido ninguna media. Necesitas al menos 1 imagen o vídeo para generar un caption.</p>
                )}
                {media.length > 0 && (
                  <div className="flex items-center gap-2">
                    {media.slice(0, 5).map((m) => (
                      <div key={m.id} className="h-12 w-12 rounded-md bg-white border border-slate-200 overflow-hidden">
                        {m.kind === 'image' ? (
                          <img src={m.url} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <video src={m.url} muted className="h-full w-full object-cover" />
                        )}
                      </div>
                    ))}
                    {media.length > 5 && (
                      <span className="text-xs text-slate-500">+{media.length - 5}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Extra info */}
              <div className="mb-5">
                <label className="text-sm font-semibold text-slate-800 mb-1.5 block">
                  ¿Quieres añadir contexto extra? <span className="text-slate-400 font-normal">(opcional)</span>
                </label>
                <textarea
                  value={extraInfo}
                  onChange={(e) => setExtraInfo(e.target.value.slice(0, 1500))}
                  rows={4}
                  placeholder="Ej: Lanzamos hoy la nueva colección de invierno, queremos enfatizar el envío gratis. Tono cercano, con un toque irónico."
                  className="input resize-y"
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-slate-400">
                    Cuanto más contexto nos des, mejor saldrá el caption. Si lo dejas vacío, solo nos guiaremos por la media.
                  </p>
                  <span className="text-[11px] text-slate-400 tabular-nums">{extraInfo.length}/1500</span>
                </div>
              </div>

              {usage && <UsagePill usage={usage} className="mb-4" />}

              <button
                onClick={run}
                disabled={!canRun}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-br from-accent-500 to-pink-500 text-white font-semibold shadow-lg shadow-accent-500/20 hover:shadow-xl hover:from-accent-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <Sparkles size={16} /> Generar caption
              </button>
              {!canRun && (
                <p className="mt-2 text-xs text-center text-slate-500">
                  {!firstAccount
                    ? 'Selecciona al menos una red social en el composer.'
                    : 'Selecciona al menos una imagen o vídeo en el composer.'}
                </p>
              )}
            </div>
          )}

          {(phase === 'prepping' || phase === 'generating') && (
            <div className="py-10 flex flex-col items-center justify-center text-center">
              <div className="relative mb-6">
                <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-accent-500 to-pink-500 flex items-center justify-center text-white shadow-xl shadow-accent-500/30">
                  <Wand2 size={26} className="animate-pulse" />
                </div>
                <div className="absolute -inset-2 rounded-3xl bg-gradient-to-br from-accent-400/30 to-pink-500/30 blur-xl animate-pulse" />
              </div>
              <div className="flex items-center gap-2 text-slate-700 font-medium">
                <Loader2 size={16} className="animate-spin" />
                {phase === 'prepping' ? 'Preparando media…' : 'Escribiendo caption…'}
              </div>
              <p className="mt-2 text-xs text-slate-500 max-w-xs">
                {phase === 'prepping'
                  ? 'Generando miniaturas de los vídeos para enviarlos a la IA.'
                  : 'Esto puede tardar entre 5 y 15 segundos. No cierres esta ventana.'}
              </p>
            </div>
          )}

          {phase === 'error' && (
            <>
              {errorCode === 'SUBSCRIPTION_REQUIRED' || errorCode === 'NOT_INCLUDED_IN_PLAN' ? (
                <UpgradeCta title={errorCode === 'SUBSCRIPTION_REQUIRED' ? 'Suscripción requerida' : 'No incluido en tu plan'} subtitle={errorMsg ?? ''} onClose={onClose} />
              ) : errorCode === 'LIMIT_REACHED' ? (
                <UpgradeCta title="Límite mensual alcanzado" subtitle={errorMsg ?? ''} onClose={onClose} />
              ) : (
                <div className="text-center py-6">
                  <div className="mx-auto h-12 w-12 rounded-full bg-red-100 flex items-center justify-center text-red-600 mb-3">
                    <AlertCircle size={22} />
                  </div>
                  <h3 className="font-semibold text-slate-900">No pudimos generar el caption</h3>
                  <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{errorMsg}</p>
                  <div className="mt-5 flex items-center justify-center gap-2">
                    {canRun && (
                      <button onClick={run} className="btn-primary">Volver a intentar</button>
                    )}
                    <button onClick={onClose} className="btn-secondary">Cerrar</button>
                  </div>
                  {usage && <UsagePill usage={usage} className="mt-5" />}
                </div>
              )}
            </>
          )}

          {phase === 'result' && result && (
            <div>
              <div className="rounded-2xl border border-accent-200 bg-gradient-to-br from-accent-50 via-white to-pink-50 p-5 mb-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {meta && Icon && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 px-2 py-0.5 rounded-full">
                        <Icon size={12} color={meta.color} /> {meta.label}
                      </span>
                    )}
                    {result.hasBrandContext && result.brandName && (
                      <span className="text-xs text-slate-500">· Marca: <span className="font-medium text-slate-700">{result.brandName}</span></span>
                    )}
                  </div>
                  <button
                    onClick={handleCopy}
                    className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5"
                  >
                    {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                    {copied ? 'Copiado' : 'Copiar'}
                  </button>
                </div>
                <p className="text-[15px] text-slate-900 leading-relaxed whitespace-pre-wrap">
                  {result.caption}
                </p>
                {result.hashtags.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-accent-200/60">
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                      <Hash size={11} /> Hashtags sugeridos
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {result.hashtags.map((h) => (
                        <span key={h} className="text-xs font-medium text-brand-700 bg-brand-50 border border-brand-100 px-2 py-0.5 rounded-full">
                          #{h}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {usage && <UsagePill usage={usage} className="mb-4" />}

              <div className="flex items-center justify-end gap-2">
                <button onClick={run} className="btn-secondary inline-flex items-center gap-1.5">
                  <Sparkles size={14} /> Generar de nuevo
                </button>
                <button onClick={handleInsert} className="btn-primary inline-flex items-center gap-1.5">
                  <Check size={14} /> Insertar en el texto
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Helpers ─────────────────────────── */

function UsagePill({ usage, className = '' }: { usage: AiCaptionUsage; className?: string }) {
  const pct = usage.limit > 0 ? Math.min(100, (usage.used / usage.limit) * 100) : 0;
  return (
    <div className={`rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 ${className}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-600">
          {usage.planName ? (
            <span className="font-medium text-slate-700">Plan {usage.planName}</span>
          ) : (
            <span className="text-slate-500">Sin suscripción</span>
          )}
        </span>
        <span className="text-slate-900 font-medium tabular-nums">
          {usage.used} / {usage.limit} este mes
        </span>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-accent-500 to-pink-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function UpgradeCta({ title, subtitle, onClose }: { title: string; subtitle: string; onClose: () => void }) {
  return (
    <div className="text-center py-4">
      <div className="mx-auto h-12 w-12 rounded-full bg-accent-100 flex items-center justify-center text-accent-700 mb-3">
        <Lock size={20} />
      </div>
      <h3 className="font-semibold text-slate-900">{title}</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{subtitle}</p>
      <div className="mt-5 flex items-center justify-center gap-2">
        <Link to="/dashboard/billing" className="btn-primary">Ver planes</Link>
        <button onClick={onClose} className="btn-secondary">Cerrar</button>
      </div>
    </div>
  );
}

